import { html, LitElement, PropertyValues } from 'lit';
import { colorVariables } from '../styles/colorVariables.js';
import {
  SkeletonAlignmentProp,
  SkeletonAnimationEffect,
  SkeletonThemeMode,
  SkeletonThemeShape,
} from '../types/props.js';

export class VeneerSkeleton extends LitElement {
  declare shape?: SkeletonThemeShape;

  declare effect?: SkeletonAnimationEffect;

  declare mode?: SkeletonThemeMode;

  declare alignment?: SkeletonAlignmentProp;

  static override properties = {
    /**
     * Sets the nested elements' shape.
     * @values round, sharp, pill
     */
    shape: { type: String },
    /**
     * Sets the nested elements' mode.
     * @values light, dark, contrast
     */
    mode: { type: String, reflect: true },
    /**
     * Sets the component's animation style.
     * @values none, pulse, wave
     */
    effect: { type: String },
    /**
     * Positions the nested elements
     * @values start, center, end
     */
    alignment: { type: String },
  };

  constructor() {
    super();
    this.shape = 'round';
    this.effect = 'wave';
    this.mode = 'light';
    this.alignment = 'start';
  }

  protected override updated(_changedProperties: PropertyValues): void {
    const components =
      'veneer-skeleton-header, veneer-skeleton-shape, veneer-skeleton-text';
    // Set the theme effect for all children components
    if (_changedProperties.has('effect')) {
      this.querySelectorAll(components).forEach(el => {
        if (
          (el.shadowRoot?.querySelectorAll('div')?.length ?? 0) > 0 &&
          this.effect
        ) {
          el.setAttribute('effect', this.effect);
        }
      });
    }

    // Set the theme shape for all children components
    if (_changedProperties.has('shape')) {
      this.querySelectorAll(components).forEach(el => {
        if (
          (el.shadowRoot?.querySelectorAll('div')?.length ?? 0) > 0 &&
          this.shape
        ) {
          el.setAttribute('theme-shape', this.shape);
        }
      });
    }

    // Set the theme alignment for all children components
    if (_changedProperties.has('alignment')) {
      this.querySelectorAll(components).forEach(el => {
        if (
          (el.shadowRoot?.querySelectorAll('div')?.length ?? 0) > 0 &&
          this.alignment
        ) {
          el.setAttribute('theme-alignment', this.alignment);
        }
      });
    }
  }

  override render() {
    return html`<slot></slot>`;
  }

  static override styles = [colorVariables];
}

if (!customElements.get('veneer-skeleton')) {
  customElements.define('veneer-skeleton', VeneerSkeleton);
}

declare global {
  interface HTMLElementTagNameMap {
    'veneer-skeleton': VeneerSkeleton;
  }
}
